import type { ApiRedeemPromocodePort } from '~/handlers/ports/api-redeem-promocode.port';
import type {
  RedeemPromocodeError,
  RedeemPromocodePayload,
  RedeemPromocodeResult,
  RedeemPromocodeSuccess,
} from '~/types/promocode';
import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';
import { getApostatudoAuthHeaders } from '~/utils/apostatudo/get-apostatudo-auth-headers';

export class ApostatudoApiRedeemPromocodeAdapter
  implements ApiRedeemPromocodePort
{
  private timeout = 10000; // 10 segundos

  // Sem retry: o resgate não é idempotente — repetir um POST cujo bônus
  // já pode ter sido creditado retornaria already_redeemed pro usuário.
  async handle(
    payload: RedeemPromocodePayload,
  ): Promise<RedeemPromocodeResult> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(
        // Sem /v1: routes/public-api-v1.php é incluído no api.php do core sem
        // prefixo de URI (o "v1" é só nome de arquivo/route-name), igual aos
        // endpoints irmãos /faq, /curated-events e /blog/posts
        `${getApostatudoApiUrl()}/promocodes/redeem`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...getApostatudoAuthHeaders(),
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        },
      );

      // Corpo lido uma vez. NÃO tratar corpo ilegível (HTML de proxy/CDN, body
      // vazio, gzip truncado) como erro de rede: o resgate pode ter sido
      // creditado server-side; cair adiante no status real (Sentry) em vez de
      // "serviço indisponível", que induziria a retry → already_redeemed.
      const body = (await response.json().catch(() => null)) as
        | (Partial<RedeemPromocodeError> & Partial<RedeemPromocodeSuccess>)
        | null;

      // Erro de negócio: o core sinaliza falha tanto via status HTTP não-2xx
      // quanto via flag success:false num corpo 200. Em ambos os casos o texto
      // exibido ao usuário vem do próprio core (body.message).
      if (!response.ok || body?.success === false) {
        return {
          ok: false,
          status: response.status,
          error: body?.error ?? null,
          message: body?.message ?? null,
        };
      }

      // 2xx fora do contrato (sem bonus ou sem nome utilizável) — tratar como
      // falha desconhecida em vez de exibir "Você recebeu: undefined".
      if (!body?.bonus?.name) {
        return {
          ok: false,
          status: response.status,
          error: null,
          message: null,
        };
      }

      return { ok: true, bonus: body.bonus };
    } catch (error) {
      // rede/timeout/CORS — o core não respondeu
      return {
        ok: false,
        status: null,
        error: null,
        message: null,
        cause: error,
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
